<script setup lang="ts">
import { AlignBottomBox, AlignTopBox, AlignVerticalCenters } from '@iconoir/vue'
import { Image as ImageIcon, Loader2, Pencil, Trash2, Type } from 'lucide-vue-next'
import { useEditor, EditorContent } from '@tiptap/vue-3'
import Placeholder from '@tiptap/extension-placeholder'
import { EImage } from '@/components/base/Email'
import { useImageCropUpload } from '@/components/ui/ImageUploader/useImageCropUpload'
import EmailImageToolbar from './EmailImageToolbar.vue'
import EmailTiptapToolbarWrapper from '../tiptap/EmailTiptapToolbarWrapper.vue'
import EmailPanelSection from '../EmailPanelSection.vue'
import { createEmailEditorExtensions } from '../../utils/tiptapExtensions'
import { emailVariables, getVariableOptions } from '../../constants/variables'
import { HeadingCleanup } from '../tiptap/HeadingCleanupExtension'
import type { GalleryImageItem, GalleryItem, GalleryTextItem, GalleryVerticalAlign, TiptapContent } from '../../types'

const props = defineProps<{
  index: number
  item: GalleryItem
  isUploading: boolean
  isSelected: boolean
  isCellSelected: boolean
  canDelete: boolean
  canSwitchType: boolean
  minHeight?: string
  cropAspectRatio?: number | [number, number]
  colorPresets?: { color: string; label: string }[]
}>()

const emit = defineEmits<{
  (e: 'upload-file', file: File): void
  (e: 'update', item: GalleryItem): void
  (e: 'remove'): void
  (e: 'select'): void
}>()

const { t } = useI18n()

const fileInputRef = ref<HTMLInputElement | null>(null)

const { cropSrc, handleFileSelected, onCropConfirm, onCropCancel } = useImageCropUpload((file) =>
  emit('upload-file', file)
)

const triggerUpload = () => fileInputRef.value?.click()

const isTextItem = computed(() => props.item.type === 'text')
const textContent = computed<TiptapContent>(() =>
  props.item.type === 'text' ? props.item.content : { type: 'doc', content: [{ type: 'paragraph' }] }
)

const variables = computed(() => getVariableOptions(emailVariables))

const textPlaceholder = t('pages.email.emailTemplateCreatePage.gallery.textPlaceholder')

const editor = useEditor({
  content: textContent.value,
  extensions: [
    ...createEmailEditorExtensions({ disableGapcursor: true }),
    HeadingCleanup,
    Placeholder.configure({
      placeholder: textPlaceholder
    })
  ],
  editorProps: {
    attributes: {
      class: 'focus:outline-none'
    }
  },
  onUpdate: ({ editor }) => {
    const currentItem = props.item
    if (currentItem.type !== 'text') return
    emit('update', { ...currentItem, content: editor.getJSON() })
  },
  onFocus: () => {
    if (!props.isCellSelected) emit('select')
  }
})

const onTextCellClick = () => {
  if (!props.isCellSelected) emit('select')
}

const onImageCellClick = () => {
  if (!props.isCellSelected) emit('select')
}

const setVerticalAlign = (verticalAlign: GalleryVerticalAlign) => {
  const currentItem = props.item
  if (currentItem.type !== 'text') return
  emit('update', { ...currentItem, verticalAlign } as GalleryTextItem)
}

const currentVerticalAlign = computed<GalleryVerticalAlign>(() =>
  props.item.type === 'text' ? props.item.verticalAlign : 'top'
)

watch(
  () => (props.item.type === 'text' ? props.item.content : null),
  (newContent) => {
    if (!newContent) return
    const ed = editor.value
    if (!ed || ed.isDestroyed) return
    if (JSON.stringify(ed.getJSON()) === JSON.stringify(newContent)) return
    ed.commands.setContent(newContent, { emitUpdate: false })
  },
  { deep: true }
)

onBeforeUnmount(() => {
  editor.value?.destroy()
})

const switchToText = () => {
  const textItem: GalleryTextItem = {
    type: 'text',
    content: { type: 'doc', content: [{ type: 'paragraph' }] },
    color: '52525b',
    align: 'left',
    verticalAlign: 'top'
  }
  emit('update', textItem)
}

const switchToImage = () => {
  const imageItem: GalleryImageItem = { type: 'image', src: '', alt: '', link: '' }
  emit('update', imageItem)
}

const imageItem = computed<GalleryImageItem | null>(() => (props.item.type === 'image' ? props.item : null))

const onImageItemUpdate = (updated: GalleryImageItem) => {
  if (props.item.type !== 'image') return
  emit('update', updated)
}

const placeholderHeight = computed(() => props.minHeight ?? 'h-40')
const isEmptyImage = computed(() => props.item.type === 'image' && !props.item.src)

const verticalAlignFlexClass = computed(() => {
  if (props.item.type !== 'text') return ''
  switch (props.item.verticalAlign) {
    case 'middle':
      return 'justify-center'
    case 'bottom':
      return 'justify-end'
    default:
      return 'justify-start'
  }
})
</script>

<template>
  <EmailTiptapToolbarWrapper
    v-if="isCellSelected && item.type === 'text'"
    :editor="editor"
    :visible="true"
    :variables="variables"
    :color-presets="colorPresets"
  />

  <Teleport v-if="isCellSelected && item.type === 'text'" to="#email-settings-panel" defer>
    <div class="border-t border-border bg-white px-4 py-4">
      <EmailPanelSection :title="t('pages.email.emailTemplateCreatePage.gallery.verticalAlignment')">
        <Tabs
          :model-value="currentVerticalAlign"
          @update:model-value="(v) => setVerticalAlign(v as GalleryVerticalAlign)"
        >
          <TabsList class="w-full">
            <TabsTrigger value="top" class="flex-1" :title="t('pages.email.emailTemplateCreatePage.gallery.alignTop')">
              <AlignTopBox />
            </TabsTrigger>
            <TabsTrigger
              value="middle"
              class="flex-1"
              :title="t('pages.email.emailTemplateCreatePage.gallery.alignMiddle')"
            >
              <AlignVerticalCenters />
            </TabsTrigger>
            <TabsTrigger
              value="bottom"
              class="flex-1"
              :title="t('pages.email.emailTemplateCreatePage.gallery.alignBottom')"
            >
              <AlignBottomBox />
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </EmailPanelSection>
    </div>
  </Teleport>

  <EmailImageToolbar
    v-if="isCellSelected && imageItem"
    :block="imageItem"
    @update:block="onImageItemUpdate"
    @replace="triggerUpload"
  >
    <template v-if="canSwitchType" #footer>
      <Button
        type="button"
        variant="ghost"
        class="w-full justify-center text-xs text-muted-foreground"
        @click="switchToText"
      >
        <Type class="size-3.5" />
        {{ t('pages.email.emailTemplateCreatePage.gallery.pickText') }}
      </Button>
    </template>
  </EmailImageToolbar>

  <div class="group/cell relative h-full min-w-0">
    <input
      ref="fileInputRef"
      type="file"
      accept="image/jpeg, image/png, image/gif, image/webp"
      class="hidden"
      @change="handleFileSelected"
    />

    <div
      v-if="item.type === 'image'"
      class="relative h-full"
      :class="{ 'ring-2 ring-offset-2 ring-border': isCellSelected }"
      @click.stop="onImageCellClick"
    >
      <div v-if="item.src" class="relative overflow-hidden rounded-lg">
        <EImage
          :src="item.src"
          :alt="item.alt"
          :style="{ width: '100%', height: 'auto', display: 'block', borderRadius: '8px' }"
        />
        <div v-if="isUploading" class="absolute inset-0 flex items-center justify-center rounded-lg bg-white/70">
          <Loader2 class="size-6 animate-spin text-primary" />
        </div>
        <div
          v-else
          class="pointer-events-none absolute inset-0 flex items-center justify-center gap-1.5 bg-black/0 opacity-0 transition-all group-hover/cell:bg-black/20 group-hover/cell:opacity-100"
        >
          <button
            type="button"
            class="pointer-events-auto inline-flex cursor-pointer items-center gap-1.5 rounded-md bg-white px-2.5 py-1 text-xs font-medium text-foreground shadow ring-1 ring-border hover:bg-muted/50"
            @click.stop="triggerUpload"
          >
            <Pencil class="size-3.5" />
            {{ t('pages.email.emailTemplateCreatePage.changeImage') }}
          </button>
        </div>
      </div>
      <div
        v-else-if="isUploading"
        class="flex h-full w-full items-center justify-center rounded-lg border-2 border-dashed border-border bg-muted"
        :class="placeholderHeight"
      >
        <Loader2 class="size-6 animate-spin text-primary" />
      </div>
      <div
        v-else
        class="flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border bg-muted p-3"
        :class="placeholderHeight"
        @click.stop="onImageCellClick"
      >
        <button
          type="button"
          class="flex flex-1 cursor-pointer flex-col items-center justify-center gap-1.5 rounded-md bg-white px-3 py-3 text-xs font-medium text-foreground ring-1 ring-border transition-colors hover:bg-muted/50 hover:ring-border"
          @click.stop="triggerUpload"
        >
          <BaseIcon name="HeroPhotoIconSolid" class="size-5 text-muted-foreground" />
          {{ t('pages.email.emailTemplateCreatePage.gallery.pickImage') }}
        </button>
        <button
          v-if="canSwitchType"
          type="button"
          class="flex flex-1 cursor-pointer flex-col items-center justify-center gap-1.5 rounded-md bg-white px-3 py-3 text-xs font-medium text-foreground ring-1 ring-border transition-colors hover:bg-muted/50 hover:ring-border"
          @click.stop="switchToText"
        >
          <Type class="size-5 text-muted-foreground" />
          {{ t('pages.email.emailTemplateCreatePage.gallery.pickText') }}
        </button>
      </div>
    </div>

    <div
      v-else-if="isTextItem"
      class="flex h-full min-h-24 w-full cursor-text flex-col rounded-lg p-3 transition"
      :class="[verticalAlignFlexClass, isCellSelected ? 'ring-2 ring-offset-2 ring-border' : '']"
      :style="{ color: `#${item.type === 'text' ? item.color : ''}` }"
      @click.stop="onTextCellClick"
    >
      <EditorContent
        :editor="editor"
        class="gallery-cell-editor w-full"
        :style="{ textAlign: item.type === 'text' ? item.align : 'left' }"
        @click.stop
      />
    </div>

    <div
      v-if="isSelected && (canDelete || (canSwitchType && !isEmptyImage))"
      class="pointer-events-none absolute top-2 right-2 z-20 flex gap-1 opacity-0 transition-opacity group-hover/cell:pointer-events-auto group-hover/cell:opacity-100"
    >
      <template v-if="canSwitchType && !isEmptyImage">
        <Button
          v-if="item.type === 'image'"
          type="button"
          size="icon-sm"
          variant="outline"
          class="bg-white shadow-sm"
          @mousedown.prevent
          @click.stop="switchToText"
        >
          <Type />
        </Button>
        <Button
          v-else
          type="button"
          size="icon-sm"
          variant="outline"
          class="bg-white shadow-sm"
          @mousedown.prevent
          @click.stop="switchToImage"
        >
          <ImageIcon />
        </Button>
      </template>
      <Button
        v-if="canDelete"
        type="button"
        size="icon-sm"
        variant="outline-destructive"
        class="bg-white shadow-sm"
        @mousedown.prevent
        @click.stop="emit('remove')"
      >
        <Trash2 />
      </Button>
    </div>

    <ImageCropDialog :src="cropSrc" :aspect-ratio="cropAspectRatio" @confirm="onCropConfirm" @cancel="onCropCancel" />
  </div>
</template>

<style scoped>
@reference "@/input.css";

.gallery-cell-editor :deep(.ProseMirror) {
  @apply outline-none min-h-6;
}

.gallery-cell-editor :deep(.ProseMirror p.is-editor-empty:first-child::before) {
  @apply text-muted-foreground italic float-left h-0 pointer-events-none;
  content: attr(data-placeholder);
}

.gallery-cell-editor :deep(p) {
  @apply m-0 text-sm leading-snug;
}

.gallery-cell-editor :deep(h1) {
  font-size: 32px;
  @apply font-bold leading-tight m-0;
}

.gallery-cell-editor :deep(h2) {
  font-size: 24px;
  @apply font-semibold leading-snug m-0;
}

.gallery-cell-editor :deep(h3) {
  font-size: 20px;
  @apply font-semibold leading-normal m-0;
}

.gallery-cell-editor :deep(h4) {
  font-size: 18px;
  @apply font-semibold leading-normal m-0;
}

.gallery-cell-editor :deep(h5) {
  font-size: 16px;
  @apply font-semibold leading-normal m-0;
}

.gallery-cell-editor :deep(h6) {
  font-size: 14px;
  @apply font-semibold leading-normal m-0;
}

.gallery-cell-editor :deep(ul) {
  @apply pl-5 my-1;
  list-style-type: disc;
}

.gallery-cell-editor :deep(ol) {
  @apply pl-5 my-1;
  list-style-type: decimal;
}

.gallery-cell-editor :deep(li) {
  @apply mb-0.5;
  display: list-item;
}

.gallery-cell-editor :deep(blockquote) {
  @apply border-l-2 border-border pl-3 my-2 text-foreground-secondary italic;
}

.gallery-cell-editor :deep(s) {
  @apply line-through;
}

.gallery-cell-editor :deep(a) {
  @apply text-blue-600 underline;
}

.gallery-cell-editor :deep(mark) {
  @apply rounded-sm px-0.5;
}

.gallery-cell-editor :deep(.variable-node),
.gallery-cell-editor :deep(.variable-tag) {
  @apply inline-flex items-center px-2 py-0.5 mx-0.5 bg-muted border border-border rounded text-sm font-medium text-foreground-secondary;
}
</style>
